import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { setSessionData, isTokenValid } from "src/api/auth";
import Loader from "src/components/Loader";
// import Widget from "../Widget/Widget";
import Chat from "../Chat";

type AuthState = "waiting" | "authenticated" | "error";

const FrontifyAuth = () => {
  const { org_slug = "" } = useParams();
  const [authState, setAuthState] = useState<AuthState>("waiting");
  const [error, setError] = useState<string>("");

  useEffect(() => {
    // Check if we already have a valid token in storage
    const alreadyAuthenticated = isTokenValid(org_slug);

    if (alreadyAuthenticated) {
      setAuthState("authenticated");
    }

    // Signal to parent that we're ready, and whether we need authentication
    if (window.parent !== window) {
      window.parent.postMessage(
        {
          type: "frontify-ready",
          authRequired: !alreadyAuthenticated,
        },
        "*",
      );
    }

    // If already authenticated, no need to listen for a token
    if (alreadyAuthenticated) return;

    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type !== "frontify-auth") return;

      const { token } = event.data;

      if (!token) {
        setError("No token provided");
        setAuthState("error");
        return;
      }

      try {
        const decoded = jwtDecode<{
          sub?: string;
          user_id?: string;
          exp?: number;
        }>(token);

        if (decoded.exp && decoded.exp < Date.now() / 1000) {
          setError("Token has expired");
          setAuthState("error");
          return;
        }

        const userId = decoded.user_id || decoded.sub || "";
        if (!userId) {
          setError("Token missing user identifier");
          setAuthState("error");
          return;
        }

        setSessionData({
          org_slug,
          sessionToken: token,
          userId,
        });

        setAuthState("authenticated");
      } catch (e) {
        console.error("Failed to process Frontify auth token:", e);
        setError("Invalid token");
        setAuthState("error");
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [org_slug]);

  if (authState === "error") {
    return <div style={{ padding: "1rem", color: "red" }}>{error}</div>;
  }

  if (authState === "waiting") {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-6 px-6 text-center">
        <Loader />
        <div className="flex flex-col gap-2">
          <h2 className="text-lg font-semibold text-gray-900">
            Preparing your account
          </h2>
          <p className="text-sm text-gray-600">
            This will only take a moment.
          </p>
        </div>
      </div>
    );
  }

  // return <Widget />;
  return <Chat hideChatHeader={true} isFrontifyWidget={true} />;
};

export default FrontifyAuth;
